import { InputNumber, Input, Select as antdSelect } from "antd";
import { colors } from "src/theme/colors";
import { line, noSteppers, radii, withAlpha } from "src/theme/ui";
import styled from "styled-components";
import TextArea from "antd/lib/input/TextArea";

/**
 * One identity column, then the three weights.
 *
 * Symbol and name were two columns naming the same thing, and in a 900px
 * dialog that left the name about 85px — so it was clipped on every row
 * whatever it was allowed, and the column that says WHAT is being weighted
 * never actually said it. They are one cell now, the ticker over its name,
 * which is how an identity reads everywhere else in the product; the freed
 * column goes to the name.
 *
 * The weight boxes take a fixed width because a percentage is four
 * characters wide and gains nothing from more. Five equal columns had given
 * a ninety-character security name exactly as much room as "100%".
 */
const columns = "minmax(0, 1.7fr) repeat(3, minmax(0, 78px)) 36px";

export const Head = styled.div`
  display: grid;
  grid-template-columns: ${columns};
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: ${columns};
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

/**
 * The builder's fields.
 *
 * Every one of them was an accent border on an accent fill — and on this
 * tenant `primary[10]` is a full periwinkle, not a tint. A form where every
 * field is marked has nothing marked: an accent is a contrast, and there was
 * nothing left for it to contrast with. The dialog's fields wear the
 * product's field styling now, with the accent kept for focus, which is the
 * one moment it means something.
 */
const field = `
  border-radius: ${radii.control}px;
  border: 1px solid ${line.hairline};
  background: ${colors.white};
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${colors.gray[300]};
  }

  &:focus,
  &.ant-input-focused,
  &.ant-input-number-focused {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }
`;

export const InputText = styled(Input)`
  ${field}
`;

export const InputTextArea = styled(TextArea)`
  ${field}
  line-height: 1.55;
`;

export const InputNum = styled(InputNumber)`
  ${field}
  ${noSteppers}
  padding: 2px 0;

  .ant-input-number-input {
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }
`;

export const Select = styled(antdSelect)`
  & .ant-select-selector {
    border-radius: ${radii.control}px !important;
    border: 1px solid ${line.hairline} !important;
    background: ${colors.white} !important;
    color: ${colors.gray[900]};
    height: 36px !important;
    box-shadow: none !important;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  &:hover .ant-select-selector {
    border-color: ${colors.gray[300]} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${colors.primary[500]} !important;
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)} !important;
  }

  & .ant-select-selection-item {
    display: flex;
    align-items: center;
  }
`;

/* A field's name, with its rule beside it rather than a red asterisk and a
   parenthetical appended to the label. A requirement is not an error until
   it has been broken. */
export const FieldLabel = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${colors.gray[900]};

  i {
    font-style: normal;
    font-weight: 400;
    color: ${colors.gray[400]};
  }
`;

export const SectionName = styled.h4`
  margin: 0 0 10px;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
`;

export const ColumnName = styled.span`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
  white-space: nowrap;
`;

/* The sum, under what it sums. */
export const TotalRow = styled(Row)`
  margin-top: 8px;
  padding-top: 10px;
  border-top: 1px solid ${line.hairline};
`;

export const Total = styled.span<{ $ok?: boolean }>`
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $ok }) => ($ok ? colors.gray[900] : colors.error[600])};
`;

export const Note = styled.p`
  margin: 10px 0 0;
  font-size: 11.5px;
  line-height: 1.55;
  color: ${colors.gray[500]};
`;

/* The ticker and its name as one cell: the dot, the select, and the name
   wrapping underneath them both. */
export const Identity = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 2px 4px;
  min-width: 0;
`;

export const SecurityName = styled.span`
  /* Under the dot as well as the select, so the name gets the cell's width
     rather than the 100px the select happens to be. */
  grid-column: 1 / -1;
  font-size: 11px;
  line-height: 1.35;
  color: ${colors.gray[500]};
  overflow-wrap: anywhere;
`;
